import * as React from 'react';

import useRouter from 'js/lib/useRouter';
import { get, isAuthenticatedUser } from 'js/lib/user';

import {
  AUTOENROLLED_COURSES_COOKIE_KEY,
  AUTOENROLLED_COURSES_COOKIE_PATH,
} from 'bundles/enterprise-legacy-learner-home/components/modals/constants';
import storageUtils from 'bundles/enterprise-legacy-learner-home/utils/storageUtils';
import ProgramMembershipsV2 from 'bundles/naptimejs/resources/programMemberships.v2';
import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useEnterpriseAutoEnrolledCourses } from 'bundles/unified-common/contexts/EnterpriseAutoEnrolledCoursesContext';
import EnterpriseModalManagerContext from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useShowEnterpriseTermsOfUseModal } from 'bundles/unified-common/hooks/useShowEnterpriseTermsOfUseModal';
import { useAvailableEnrollmentOptions } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseAvailableEnrollmentProgramsContext';
import { useMiniModalRedirectData } from 'bundles/unified-home-common/contexts/enterprise/MiniModalRedirectContext';

type Props = {
  children: React.ReactNode;
};

/*
This provider aims to find out if we need to surface a modal to the user.
If it's determined we should show multiple modals, it decides priority.
We use this value primarily in bundles/unified-common/components/EnterpriseModalManager, which
uses this value to render the individual modals.
*/
const EnterpriseModalManagerProvider = ({ children }: Props) => {
  const router = useRouter();
  // This state property is driven by the ProgramAccessModal that ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS maps to.
  const [isAccessModalDismissed, setIsAccessModalDismissed] = React.useState(false);
  // This state property is driven by the AutoEnrolledCoursesModal that ENTERPRISE_MODAL_NAMES.AUTOENROLLED_COURSES maps to.
  const [isAutoenrolledCoursesModalDismissed, setIsAutoenrolledCoursesModalDismissed] = React.useState(false);
  const [hasShownLoginModal, setHasShownLoginModal] = React.useState(false);
  const features = useEnterpriseProductFeatures();
  const programMembership = useProgramMembership();
  const naptimeProgramMembership = new ProgramMembershipsV2(programMembership?.membership ?? {});
  const autoEnrolledCourses = useEnterpriseAutoEnrolledCourses();
  const availableEnrollmentOptions = useAvailableEnrollmentOptions();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isInLoginMode = router.location.query.authMode === 'login';
  const isComingFromExternalPlatforms = router.location.query.isExternal === 'true';

  const {
    isLoading: isMiniModalDataLoading,
    courseSlug: miniModalCourseSlug,
    s12nSlug: miniModalS12nSlug,
  } = useMiniModalRedirectData();

  // In the optimized learner flow, we do not show the program join modal on the unified XDP.
  // If a user is eligible to join the current program (program in slug), then upon clicking the XDP enroll button,
  // we enroll the user into the program and then into the course directly in the background (without showing the program join modal).
  const showOptimisedLearnerEnrollmentFlow = isComingFromExternalPlatforms && !!features?.enableOptimizedLearnerFlow;
  const canEnrollIntoCurrentProgramOnXdp =
    showOptimisedLearnerEnrollmentFlow && naptimeProgramMembership?.isEligibleToJoinProgram;

  const { showEnterpriseTermsOfUseModal, setShowEnterpriseTermsOfUseModal } =
    useShowEnterpriseTermsOfUseModal(thirdPartyOrganization);

  // Determine if ENTERPRISE_MODAL_NAMES.PRODUCT_DESCRIPTION_PAGE_REDIRECT is able to be shown.
  // We want to redirect to the product description page if legacy query params are present for mini modal
  // and certain other conditions are met. This is only used in legacy contexts and should eventually
  // be deprecated.
  const redirectToProductDescriptionPage =
    router.location.query.productId &&
    router.location.query.showMiniModal &&
    !isMiniModalDataLoading &&
    !hasShownLoginModal &&
    (miniModalCourseSlug || miniModalS12nSlug) &&
    !isInLoginMode &&
    (features?.isBrowseOnlyProgram ||
      canEnrollIntoCurrentProgramOnXdp ||
      !naptimeProgramMembership.hasModalToDisplay ||
      isAccessModalDismissed);

  // Determine if ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS is able to be shown.
  // Do not show this modal when in specific case on enterprise unified XDP where user is not member of current program,
  // but is member of other programs in current org and has other options to enroll.
  // availableEnrollmentOptions.programs will have data if this is the case.
  // In this scenario, this modal will surface the "program deny modal". This is not relevant for learners in this case
  // since they can enroll and could cause confusion.
  const showProgramAccessModal =
    !features?.isBrowseOnlyProgram &&
    !isInLoginMode &&
    !!naptimeProgramMembership &&
    !isMiniModalDataLoading &&
    naptimeProgramMembership.hasModalToDisplay &&
    !isAccessModalDismissed &&
    !programMembership?.isMembershipLoading &&
    !availableEnrollmentOptions?.isLoading &&
    !canEnrollIntoCurrentProgramOnXdp &&
    !availableEnrollmentOptions?.programs?.length &&
    !availableEnrollmentOptions?.invitedPrograms?.length;

  // Determine if ENTERPRISE_MODAL_NAMES.LOGIN is able to be shown.
  // Show login modal if the incoming URL wants to show the legacy mini modal,
  // the user is logged out and we were unable to find product slug info.
  // This likely means the user is trying to view a private course.
  const showLoginModal =
    !isAuthenticatedUser() &&
    router.location.query.productId &&
    router.location.query.showMiniModal &&
    !isInLoginMode &&
    !hasShownLoginModal &&
    !isMiniModalDataLoading &&
    !miniModalCourseSlug &&
    !miniModalS12nSlug &&
    (features?.isBrowseOnlyProgram || !naptimeProgramMembership.hasModalToDisplay || isAccessModalDismissed);

  // Check to see if there are any autoenrollment course triggers stored, and compare it to the
  // autoenrolledCourseIds to see if there are any new autoenrollment triggers to show
  const autoEnrolledModalCookieValue: string[] = storageUtils.get(
    get()?.id || 0,
    AUTOENROLLED_COURSES_COOKIE_PATH,
    AUTOENROLLED_COURSES_COOKIE_KEY,
    []
  );
  const filteredAutoEnrolledCourses = autoEnrolledCourses?.filter(
    (course) => !autoEnrolledModalCookieValue.includes(course.id)
  );

  // Determine if ENTERPRISE_MODAL_NAMES.AUTOENROLLED_COURSES is able to be shown.
  const showAutoEnrolledCoursesModal =
    !features?.isBrowseOnlyProgram &&
    !isInLoginMode &&
    !showOptimisedLearnerEnrollmentFlow &&
    programMembership.isProgramMember &&
    Boolean(filteredAutoEnrolledCourses && filteredAutoEnrolledCourses.length > 0) &&
    !isAutoenrolledCoursesModalDismissed;

  // Determine if ENTERPRISE_MODAL_NAMES.ADD_EMAIL is able to be shown.
  // The modal this maps to has some additional logic for display.
  // Eventually, when we refactor the modals themselves we can move that logic here.
  const showAddEmailModal =
    !features?.isBrowseOnlyProgram &&
    !isInLoginMode &&
    (isAccessModalDismissed || !naptimeProgramMembership.hasModalToDisplay);

  const isNewTouModalEnabled = features?.enableEnterpriseTermsOfUseModal || features?.enableOptimizedLearnerFlow;
  // Determine if ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE is able to be shown.
  // This will currently be controlled by a feature toggle which will then go on
  // to be enabled for all of enterprise
  const shouldShowEnterpriseTermsOfUseModal =
    isAuthenticatedUser() &&
    isNewTouModalEnabled &&
    showEnterpriseTermsOfUseModal &&
    naptimeProgramMembership.isEligibleToJoinProgram;

  let modalToShow = null;

  // Multiple values can be true, choose one modal to show based on priority.
  if (shouldShowEnterpriseTermsOfUseModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE;
  } else if (showProgramAccessModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS;
  } else if (showLoginModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.LOGIN;
  } else if (redirectToProductDescriptionPage) {
    modalToShow = ENTERPRISE_MODAL_NAMES.PRODUCT_DESCRIPTION_PAGE_REDIRECT;
  } else if (showAutoEnrolledCoursesModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.AUTOENROLLED_COURSES;
  } else if (showAddEmailModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.ADD_EMAIL;
  }

  return (
    <EnterpriseModalManagerContext.Provider
      value={{
        modalToShow,
        isAccessModalDismissed,
        setIsAccessModalDismissed,
        isAutoenrolledCoursesModalDismissed,
        setIsAutoenrolledCoursesModalDismissed,
        setHasShownLoginModal,
        setShowEnterpriseTermsOfUseModal,
        showEnterpriseTermsOfUseModal,
      }}
    >
      {children}
    </EnterpriseModalManagerContext.Provider>
  );
};
export default EnterpriseModalManagerProvider;
